import { captureException } from '@sentry/browser';
import { useState } from 'react';
import { Installments } from 'src/components/OrderSummary/types';
import { useSalesChannel } from 'src/components/SalesChannel/SalesChannel';
import { SHOPLESS_RESELLER_ID } from 'src/services/analytics/channelInfo';
import { Bundle, IncludedProduct, InstallmentPlanPricing, Money, Product, ProductPricing, RecommendedAddonProduct, SoftwarePlan, TransactionRate, fetchBundlesDetail, fetchProductsDetail } from 'src/services/dcs/bundle';
import { getField } from 'src/utils/EntryUtil';
import useDeepCompareEffect from 'use-deep-compare-effect';

export interface BundlePricings {
  id: string;
  name: string;
  basePrice: Money;
  monthlyAdditionalPrice: Money;
  additionalDevicePrice?: Money;
  installmentPlans: InstallmentPlanPricing;
  keyedInRate: TransactionRate;
  inPersonRate: TransactionRate;
  msrp: Money | null;
  softwarePlan: SoftwarePlan;
  includedProducts: IncludedProduct[];
  recommendedAddons: RecommendedAddonProduct[];
  isSelfServicePurchaseEnabled?: boolean;
}

export interface ProductPricings {
  id: string;
  name: string;
  basePrice: Money;
  installmentPlans: InstallmentPlanPricing;
  msrp: Money | null;
  fdmpId: string;
  isSelfServicePurchaseEnabled?: boolean;
  accessories?: Product[];
}

interface UseDCS {
  bundlesPricings: Array<BundlePricings | undefined>;
  productsPricings: Array<ProductPricings | undefined>;
}

export interface DcsPricingQuery { bundleIds: string[]; productIds: string[]; }

export function useDCS({ bundleIds = [], productIds = [] }: { bundleIds?: Array<string | undefined>; productIds?: Array<string | undefined>}): UseDCS {
  const { salesChannel } = useSalesChannel();
  const resellerId = getField(salesChannel, 'cloverResellerId');
  const [bundlesPricings, setBundlesPricings] = useState<Array<BundlePricings | undefined>>([]);
  const [productsPricings, setProductsPricings] = useState<Array<ProductPricings | undefined>>([]);

  const getInstallmentPlanMap = (productPricing: ProductPricing[]) => {
    return productPricing.reduce((map, pricing) => {
      if (pricing.installmentPlan.code !== Installments.P) {
        map[pricing.installmentPlan.code] = pricing.price;
      }
      return map;
    }, {});
  };

  const getBundlePriceDetail = (bundle?: Bundle) => {
    if (!bundle) return;

    const pricing = bundle.productPricing.find(pricing => pricing.installmentPlan.code === Installments.P);

    if (!pricing) return;

    const basePrice = pricing.price;
    const monthlyAdditionalPrice = bundle.softwarePlanPricing.monthlyPrice;
    const additionalDevicePrice = bundle.softwarePlanPricing.additionalDevicePrice;
    const installmentPlans: InstallmentPlanPricing = getInstallmentPlanMap(bundle.productPricing);

    const { keyedInRate, inPersonRate } = bundle.softwarePlanPricing.transactionRates;
    const { id, name, msrp, includedProducts, recommendedAddons, softwarePlan, isSelfServicePurchaseEnabled } = bundle;

    return {
      id,
      name,
      basePrice,
      monthlyAdditionalPrice,
      msrp,
      keyedInRate,
      inPersonRate,
      installmentPlans,
      softwarePlan,
      includedProducts,
      recommendedAddons,
      isSelfServicePurchaseEnabled,
      additionalDevicePrice,
    } as BundlePricings;
  };

  const getProductPriceDetail = (product?: Product) => {
    if (!product) return;

    const pricing = product.pricing.find(pricing => pricing.installmentPlan.code === Installments.P);

    if (!pricing) return;

    const basePrice = pricing.price;
    const installmentPlans: InstallmentPlanPricing = getInstallmentPlanMap(product.pricing);

    const { id, name, msrp, isSelfServicePurchaseEnabled, isIncludedInMultiDevicePricing, fdmpId, supportedSoftwarePlans, accessories } = product;

    return {
      id,
      name,
      basePrice,
      msrp,
      installmentPlans,
      isSelfServicePurchaseEnabled,
      isIncludedInMultiDevicePricing,
      fdmpId,
      supportedSoftwarePlans,
      accessories,
    } as ProductPricings;
  };

  useDeepCompareEffect(() => {
    if (!bundleIds.length) {
      setBundlesPricings([]);
      return;
    }

    async function fetchBundles(bundleIds: Array<string | undefined>, resellerId: string) {
      const { data: bundlesDetail } = await fetchBundlesDetail({
        resellerId,
        bundleIds: bundleIds.filter(Boolean) as string[],
      });
      const bundlesPricings = bundleIds.map(bundleId => {
        if (!bundleId) return;
        const bundleDetail = bundlesDetail.find(bundleDetail => bundleDetail.id === bundleId);
        return getBundlePriceDetail(bundleDetail);
      });
      setBundlesPricings(bundlesPricings);
    }

    try {
      if (resellerId && resellerId !== SHOPLESS_RESELLER_ID) fetchBundles(bundleIds, resellerId);
    } catch (error) {
      console.error(error);
      captureException('Failed to fetch Bundle pricing');
    }
  }, [resellerId, bundleIds]);

  useDeepCompareEffect(() => {
    if (!productIds.length) {
      setProductsPricings([]);
      return;
    }

    async function fetchProducts(productIds: Array<string | undefined>, resellerId: string) {
      const { data: productsDetail } = await fetchProductsDetail({
        resellerId,
        productIds: productIds.filter(Boolean) as string[],
      });
      const productsPricings = productIds.map(productId => {
        if (!productId) return;
        const productDetail = productsDetail.find(productDetail => productDetail.id === productId);
        return getProductPriceDetail(productDetail);
      });
      setProductsPricings(productsPricings);
    }

    try {
      if (resellerId && resellerId !== SHOPLESS_RESELLER_ID) fetchProducts(productIds, resellerId);
    } catch (error) {
      console.error(error);
      captureException('Failed to fetch product pricing');
    }
  }, [resellerId, productIds]);

  return {
    bundlesPricings,
    productsPricings,
  };
}
